import { OnboardingEventId } from '@thoughtspot/onboarding-event-service';
import { TourSteps } from '@thoughtspot/radiant-react/widgets/tour';
import { getCreateInsightsTourChain } from './create-insights-steps';
import { getEmbedInsightsTourChain } from './embed-insights-steps';
import { getExploreDashboardsTourChain } from './explore-dashboards-steps';
import { getExploreInsightsTourChain } from './explore-insights-steps';
import { getSpotterChatTourChain } from './spotter-chat-steps';
import { getSpotterCodeTourChain } from './spotter-code-steps';
import { getSpotterVizTourChain } from './spotter-viz-steps';
import { getTeamOfAgentsTourChain } from './team-of-agents-steps';

export const LAYOUT_SETTLE_DELAY_MS = 300;
export const WAIT_FOR_ELEMENT_SAFETY_TIMEOUT_MS = 80000;

export const EXPLORE_INSIGHTS_USE_ENGAGE_EVENT_URL =
    'https://docs.thoughtspot.com/cloud/latest/thoughtspot-plugins';

export const delay = (ms: number): Promise<void> =>
    new Promise(resolve => {
        setTimeout(resolve, ms);
    });

export enum TourPersonaPrefix {
    SPOTTER_CHAT = 'spotter_chat',
    TEAM_OF_AGENTS = 'team_of_agents',
    SPOTTER_VIZ = 'build_with_spotterviz',
    EXPLORE_DASHBOARDS = 'explore_dashboards',
    EXPLORE_INSIGHTS = 'explore_insights',
    CREATE_INSIGHTS = 'create_insights',
    EMBED_INSIGHTS = 'embed_insights',
    EMBED_WITH_SPOTTERCODE = 'embed_with_spottercode',
}

export enum TourChainActionType {
    CLICK = 'click',
    HOVER = 'hover',
}

export interface TourChainAction {
    selector: string;
    type: TourChainActionType;
}

export interface TourChainStep {
    steps: TourSteps[];
    nextUrl?: string;
    beforeAction?: TourChainAction;
    beforeActions?: TourChainAction[];
    afterAction?: TourChainAction;
    afterActions?: TourChainAction[];
    dismissOnAfterClick?: boolean;
    /** Dismiss the tour step (via click or Enter) when the user interacts
     *  with the specified element. */
    dismissOnAction?: TourChainAction;
    /** When true, wait for the target element to be removed from the DOM
     *  before waiting for it to reappear. Use when the same selector exists
     *  both before and after a page transition (e.g. edit mode). */
    waitForTargetRemoval?: boolean;
    /** Fired via sendOnboardingEvent when the user clicks "Got it"
     *  on the last step of this chain entry. */
    onCompleteEventId?: OnboardingEventId;
    /** Override the label shown on the last-step button (e.g. "Next",
     *  "Try it out"). Falls back to the container's default ("Got it"). */
    doneButtonText?: string;
    /** Override the label shown on intermediate-step buttons.
     *  Falls back to the Tour component's default ("Next"). */
    nextButtonText?: string;
    /** Show step progress (e.g. "1 of 3") in the tooltip footer. */
    showProgress?: boolean;
    showOnTourClose?: boolean;
    /** If true, the tour step will still render even when a ReactModalPortal
     *  is open (e.g. when the tour target lives inside a modal). */
    showInsideModal?: boolean;
    /** If provided, evaluated at runtime after the target element
     *  appears. Return false to skip this chain entry. */
    condition?: () => boolean;
    path?: string[];
    /** Element to wait for before showing the tour. Overrides steps[0].target. */
    readyTarget?: string;
}

/**
 * Watches the DOM via MutationObserver until an element matching
 * `selector` appears.
 *
 * @param signal  - AbortSignal to cancel the wait (e.g. on effect
 *                  cleanup or tour cancellation).
 * @param timeoutMs - Optional safety-net timeout. If provided, the
 *                    observer gives up after this many ms even when
 *                    neither the element nor the signal resolved it.
 */
export const waitForElement = (
    selector: string,
    signal?: AbortSignal,
    timeoutMs?: number,
): Promise<Element | null> => {
    return new Promise(resolve => {
        if (signal?.aborted) {
            resolve(null);
            return;
        }

        const existing = document.querySelector(selector);
        if (existing) {
            resolve(existing);
            return;
        }

        let settled = false;
        const observer = new MutationObserver(() => {
            const el = document.querySelector(selector);
            if (!el || settled) return;
            settled = true;
            observer.disconnect();
            resolve(el);
        });

        signal?.addEventListener('abort', () => {
            if (settled) return;
            settled = true;
            observer.disconnect();
            resolve(null);
        });

        observer.observe(document.body, {
            childList: true,
            subtree: true,
        });

        if (timeoutMs !== undefined) {
            setTimeout(() => {
                if (settled) return;
                settled = true;
                observer.disconnect();
                resolve(null);
            }, timeoutMs);
        }
    });
};

export const performTourAction = async (
    action: TourChainAction,
    signal?: AbortSignal,
    timeoutMs?: number,
): Promise<boolean> => {
    const el = await waitForElement(action.selector, signal, timeoutMs);
    if (!(el instanceof HTMLElement)) return false;

    if (action.type === TourChainActionType.HOVER) {
        el.dispatchEvent(new MouseEvent('mouseenter', { bubbles: true }));
        el.dispatchEvent(new MouseEvent('mouseover', { bubbles: true }));
    } else {
        el.click();
    }
    return true;
};

export const waitForElementRemoval = (
    selector: string,
    signal?: AbortSignal,
    timeoutMs?: number,
): Promise<boolean> =>
    new Promise(resolve => {
        if (signal?.aborted) {
            resolve(false);
            return;
        }

        if (!document.querySelector(selector)) {
            resolve(true);
            return;
        }

        let settled = false;
        const observer = new MutationObserver(() => {
            if (document.querySelector(selector) || settled) return;
            settled = true;
            observer.disconnect();
            resolve(true);
        });

        signal?.addEventListener('abort', () => {
            if (settled) return;
            settled = true;
            observer.disconnect();
            resolve(false);
        });

        observer.observe(document.body, {
            childList: true,
            subtree: true,
        });

        if (timeoutMs !== undefined) {
            setTimeout(() => {
                if (settled) return;
                settled = true;
                observer.disconnect();
                resolve(false);
            }, timeoutMs);
        }
    });

/**
 * Executes a sequence of actions, waiting for each action's target
 * element to appear in the DOM before performing it.
 * Useful when a tour target is only visible after multiple
 * intermediate interactions (e.g. 3-dots menu → Edit → modal).
 */
export const performSequentialActions = (
    actions: TourChainAction[],
    signal?: AbortSignal,
    timeoutMs?: number,
): Promise<boolean> =>
    actions.reduce<Promise<boolean>>(
        (prev, action) =>
            prev.then(async success => {
                if (!success || signal?.aborted) return false;

                const el = await waitForElement(
                    action.selector,
                    signal,
                    timeoutMs,
                );
                if (!el || !(el instanceof HTMLElement)) return false;

                if (action.type === TourChainActionType.HOVER) {
                    el.dispatchEvent(
                        new MouseEvent('mouseenter', { bubbles: true }),
                    );
                    el.dispatchEvent(
                        new MouseEvent('mouseover', { bubbles: true }),
                    );
                } else {
                    el.click();
                }
                return true;
            }),
        Promise.resolve(true),
    );

export const getChecklistTourChain = (
    taskId: string,
    t: (key: string, params?: Record<string, unknown>) => string,
): TourChainStep[] | null => {
    if (taskId.startsWith(TourPersonaPrefix.SPOTTER_CHAT)) {
        return getSpotterChatTourChain(taskId, t);
    }
    if (taskId.startsWith(TourPersonaPrefix.TEAM_OF_AGENTS)) {
        return getTeamOfAgentsTourChain(taskId, t);
    }
    if (taskId.startsWith(TourPersonaPrefix.SPOTTER_VIZ)) {
        return getSpotterVizTourChain(taskId, t);
    }
    if (taskId.startsWith(TourPersonaPrefix.EXPLORE_DASHBOARDS)) {
        return getExploreDashboardsTourChain(taskId, t);
    }
    if (taskId.startsWith(TourPersonaPrefix.EXPLORE_INSIGHTS)) {
        return getExploreInsightsTourChain(taskId, t);
    }
    if (taskId.startsWith(TourPersonaPrefix.CREATE_INSIGHTS)) {
        return getCreateInsightsTourChain(taskId, t);
    }
    if (taskId.startsWith(TourPersonaPrefix.EMBED_INSIGHTS)) {
        return getEmbedInsightsTourChain(taskId, t);
    }
    if (taskId.startsWith(TourPersonaPrefix.EMBED_WITH_SPOTTERCODE)) {
        return getSpotterCodeTourChain(taskId, t);
    }
    return null;
};
